import { findBestMatch } from '../bankUtils';

// ─── Levenshtein distance ────────────────────────────────────────────────────
export function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return dp[m][n];
}

// ─── Normalize name ───────────────────────────────────────────────────────────
export const normName = s =>
  (s || '').toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9 ]/g, ' ').trim().replace(/\s+/g, ' ');

// ─── Type config ──────────────────────────────────────────────────────────────
export const TYPE_CONFIG = {
  A:      { label: 'Cotisation VIR',      color: '#166534', bg: '#dcfce7', icon: '💰' },
  B:      { label: 'Impayé',              color: '#991b1b', bg: '#fee2e2', icon: '❌' },
  C:      { label: 'Remise SEPA',         color: '#1d4ed8', bg: '#dbeafe', icon: '📦' },
  D:      { label: 'Frais bancaires',     color: '#d97706', bg: '#fef3c7', icon: '🏦' },
  D_PREL: { label: 'Frais prél. impayé', color: '#b45309', bg: '#ffedd5', icon: '🏦' },
  E:      { label: 'Dépense / Achat',     color: '#7c3aed', bg: '#ede9fe', icon: '🛒' },
  F:      { label: 'Reversement GD-FO',  color: '#0369a1', bg: '#e0f2fe', icon: '🔄' },
};

// ─── Categories ───────────────────────────────────────────────────────────────
export const CATEGORIES = [
  'Cotisation',
  'Remise prélèvement SEPA',
  'Reversement fédéral GD-FO',
  'Frais bancaires',
  'Frais bancaires - prélèvement',
  'Frais postaux',
  'Achat cartes / timbres',
  'Subvention UD',
  'Subvention GD',
  'Subvention UNSFO',
  'Participation camarades',
  'Fournitures de bureau',
  'Communication',
  'Formation syndicale',
  'Déplacements',
  'Réceptions / AG',
  'Frais manifestation',
  'Cotisation organisme',
  'Subvention versée',
  'Matériel informatique',
  'Divers',
];

export const MOIS_LABELS = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

// ─── Classify a BFC statement line ───────────────────────────────────────────
export function classifyBFCLine(lib) {
  const L = lib.toUpperCase();
  // ── Frais liés aux impayés prélèvement → D_PREL (AVANT les tests génériques) ──
  if (/EXT\s+FRAIS\s+REMISE\s+PREL/.test(L))                                        return 'D_PREL';
  if (/FRAIS\s+IMPAYE\s*PRELEVEMENT/.test(L))                                        return 'D_PREL';
  if (/FRAIS\s+IMPAYE\b/.test(L))                                                    return 'D_PREL';
  // ── Virements reçus (cotisations) ──
  if (/VIRT?\s*DE\b|VIRT?\s+INSTANTANE\s+DE\b|VIREMENT\s*RE[CÇ]U\s*DE|VIR\s+INST|VIR\s*RECU/.test(L)) return 'A';
  // ── Impayés adhérents ──
  if (/IMPAYE\s*PREL|IMP\s*PREL|PROVISION\s*INSUF|REJET\s*ORDRE\s*TIRE\s*SDD|REJET\s*PRELEVEMENT/.test(L)) return 'B';
  // ── Frais bancaires génériques (hors prélèvement) ──
  if (/EXT\s+FRAIS/.test(L))                                                         return 'D';
  // ── Remises SEPA ──
  if (/REMISE\s*PREL|REMISE\s*PRELEVEMENT/.test(L))                                  return 'C';
  // ── Autres frais bancaires ──
  if (/COMM.*PREL\s*SEPA|BFCNET|ABO\s*BFC|FACTUR\s*CONNEXION|EXT\s*FRAIS\s*REMISE|FRAIS\s*TENUE/.test(L)) return 'D';
  // ── Reversement GD-FO ──
  if (/VIR\s*FAV\s*GD.FO|GD.FO.REUNION|GRAND.FO/.test(L))                           return 'F';
  return 'E';
}

const catFromType = { A:'Cotisation', B:'Impayé', C:'Remise prélèvement SEPA', D:'Frais bancaires', D_PREL:'Frais bancaires - prélèvement', E:'Divers', F:'Reversement fédéral GD-FO' };

// ─── Parse BFC OI statement text ─────────────────────────────────────────────
export function parseBFCStatement(fullText, uidFn) {
  const lines = fullText.split('\n').map(l => l.trim()).filter(l => l.length > 2);

  const rDate    = /^(\d{2})\/(\d{2})(?:\/(\d{2,4}))?/;
  const rAmount  = /(\d{1,3}(?:\s\d{3})*[,\.]\d{2})/g;
  const rSkip    = /^(SOLDE|REPORT|TOTAL|ANCIEN|NOUVEAU|PAGE\s*\d|IBAN|SWIFT|TITULAIRE|PERIODE|EXTRAIT|RELEVE\s*N|VOTRE\s*COMPTE|DEBIT|CREDIT|DATE\s+LIBELL)/i;
  const rSoldePrec = /^Solde\s+Pr[eé]c[eé]dent\b/i;
  const rSoldeFin  = /^Nouveau\s+solde\s+CREDIT/i;
  const rAmtSimple = /(\d{1,3}(?:\s\d{3})*[,\.]\d{2})/;

  let soldeOuverture   = null;
  let soldeFinal       = null;
  let totalDebits_PDF  = null;
  let totalCredits_PDF = null;

  // Group lines into transactions
  const transactions = [];
  let cur = null;
  for (const line of lines) {
    // Capture opening/closing balance before skipping
    if (rSoldePrec.test(line) && soldeOuverture === null) {
      const m = line.match(rAmtSimple);
      if (m) soldeOuverture = parseFloat(m[1].replace(/\s/g,'').replace(',','.'));
    }
    if (rSoldeFin.test(line)) {
      const m = line.match(rAmtSimple);
      if (m) soldeFinal = parseFloat(m[1].replace(/\s/g,'').replace(',','.'));
    }
    // Capture totaux mouvements BFC (pour contrôle de cohérence — lignes TOTAL skippées ensuite)
    if (totalDebits_PDF  === null && /TOTAL.*MOUVEMENT.*DEB/i.test(line)) {
      const m = line.match(rAmtSimple);
      if (m) totalDebits_PDF = parseFloat(m[1].replace(/\s/g,'').replace(',','.'));
    }
    if (totalCredits_PDF === null && /TOTAL.*MOUVEMENT.*CR[EÉ]D/i.test(line)) {
      const m = line.match(rAmtSimple);
      if (m) totalCredits_PDF = parseFloat(m[1].replace(/\s/g,'').replace(',','.'));
    }
    if (rSkip.test(line) && !rDate.test(line)) continue;
    const dm = line.match(rDate);
    if (dm) {
      if (cur) transactions.push(cur);
      cur = { dateStr: dm[0], parts: [line.replace(rDate, '').trim()] };
    } else if (cur) {
      cur.parts.push(line);
    }
  }
  if (cur) transactions.push(cur);

  const ops = [];
  for (const t of transactions) {
    const fullLib = t.parts.join(' ').replace(/\s+/g, ' ').trim();

    // CRITIQUE : supprimer les dates JJ/MM avant d'extraire les montants
    // "02/01 12,00" → sans strip : regex capte "01 12,00" → parseFloat("0112.00") = 112 ❌
    const fullLibNoDate = fullLib.replace(/\b\d{2}\/\d{2}\b/g, ' ');
    const allAmounts = [...fullLibNoDate.matchAll(rAmount)]
      .map(m => parseFloat(m[1].replace(/\s/g,'').replace(',','.')))
      .filter(n => n > 0.01 && n < 999999);

    if (!allAmounts.length) continue;

    const type = classifyBFCLine(fullLib);
    const isCredit = ['A','C'].includes(type);
    // BFC OI format: [date] [libellé] [date_valeur] [débit_ou_crédit] [SOLDE_INTERMEDIAIRE]
    // Dernier montant = solde intermédiaire → ignorer
    // Avant-dernier montant = montant réel de l'opération
    const amount = allAmounts.length >= 2 ? allAmounts[allAmounts.length - 2] : allAmounts[0];

    // Clean libellé: strip amounts and secondary dates
    let libelleBrut = fullLib
      .replace(/(\d{1,3}(?:\s\d{3})*[,\.]\d{2})/g, '')
      .replace(/\d{2}\/\d{2}(\/\d{2,4})?/g, '')
      .replace(/\s+/g, ' ').trim().slice(0, 60);

    if (libelleBrut.length < 2) continue;

    // Try to normalize date to ISO
    const [dd, mm, yy] = t.dateStr.split('/');
    const yyyy = yy ? (yy.length === 2 ? `20${yy}` : yy) : new Date().getFullYear().toString();
    const isoDate = `${yyyy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`;

    ops.push({
      id: uidFn(),
      date: isoDate,
      dateAffichage: t.dateStr,
      libelleBrut,
      fullLibelle: t.parts[0]?.slice(0, 80) || libelleBrut,
      type,
      categorie: catFromType[type] || 'Divers',
      debit:  isCredit ? 0 : amount,
      credit: isCredit ? amount : 0,
      adherentId: null,
      matchStatus: 'none',
      matchScore: 999,
      statut: 'en_attente',
      note: '',
      pieceJointe: null,
      source: 'import',
      pointe: false,
      validated: false,
    });
  }

  return { ops, soldeOuverture, soldeFinal, totalDebits_PDF, totalCredits_PDF };
}

// ─── Données historiques (2021-2024 fixe, 2025 modifiable) ─────────────────
export const HIST_YEARS = [2021, 2022, 2023, 2024, 2025];
export const HIST_DEFAULT = {
  cotisations:      { 2021: 19094.90, 2022: 18058.25, 2023: 35846.00, 2024: 37756.10 },
  subvention_ud:    { 2021: 340.00,   2022: 0,        2023: 1000.00,  2024: 1200.00  },
  subvention_gd:    { 2021: 1211.75,  2022: 0,        2023: 0,        2024: 1000.00  },
  subvention_unsfo: { 2021: 3478.35,  2022: 0,        2023: 4756.78,  2024: 1188.56  },
  participation:    { 2021: 0,        2022: 0,        2023: 1000.00,  2024: 0        },
  cartes_timbres:   { 2021: 12519.00, 2022: 15334.11, 2023: 22854.70, 2024: 23909.60 },
  service_bancaire: { 2021: 556.37,   2022: 444.62,   2023: 676.92,   2024: 696.15   },
  frais_prelevement:{ 2021: 42.70,    2022: 0,        2023: 1558.30,  2024: 2888.57  },
  frais_postaux:    { 2021: 0,        2022: 61.00,    2023: 0,        2024: 0        },
  fournitures:      { 2021: 164.37,   2022: 1556.16,  2023: 2950.91,  2024: 39.99    },
  manifestation:    { 2021: 1401.70,  2022: 0,        2023: 117.70,   2024: 174.00   },
  deplacement:      { 2021: 2646.90,  2022: 0,        2023: 8852.45,  2024: 3244.71  },
  restauration:     { 2021: 498.45,   2022: 873.68,   2023: 2456.95,  2024: 4231.85  },
  cotisation_unsfo: { 2021: 629.28,   2022: 162.64,   2023: 712.24,   2024: 547.20   },
  subvention_versee:{ 2021: 200.00,   2022: 0,        2023: 200.00,   2024: 0        },
};

// ─── Lignes Compte de Résultat ───────────────────────────────────────────────
export const RECETTES_LIGNES = [
  { id: 'cotisations',      libelle: 'Cotisations collectées',          types: ['A','C'], categories: ['Cotisation','Remise prélèvement SEPA'], dir: 'credit' },
  { id: 'subvention_ud',    libelle: "Subvention de l'UD FO Réunion",  categories: ['Subvention UD'],   dir: 'credit' },
  { id: 'subvention_gd',    libelle: 'Subvention du GD FO Réunion',    categories: ['Subvention GD'],   dir: 'credit' },
  { id: 'subvention_unsfo', libelle: "Subvention de l'UNSFO",          categories: ['Subvention UNSFO'],dir: 'credit' },
  { id: 'participation',    libelle: 'Participation des camarades',     categories: ['Participation camarades'], dir: 'credit' },
];
export const DEPENSES_LIGNES = [
  { id: 'cartes_timbres',    libelle: 'Achat de cartes timbres',                        types: ['F'], categories: ['Achat cartes / timbres','Reversement fédéral GD-FO'], dir: 'debit' },
  { id: 'service_bancaire',  libelle: 'Service bancaire',                               categories: ['Frais bancaires'], dir: 'debit' },
  { id: 'frais_prelevement', libelle: 'Frais de prélèvement',                           types: ['D_PREL'], categories: ['Frais bancaires - prélèvement'], dir: 'debit' },
  { id: 'frais_postaux',     libelle: 'Frais postaux',                                  categories: ['Frais postaux'], dir: 'debit' },
  { id: 'fournitures',       libelle: 'Fournitures activité & petit matériel',          categories: ['Fournitures de bureau','Matériel informatique'], dir: 'debit' },
  { id: 'manifestation',     libelle: 'Frais de manifestation',                         categories: ['Frais manifestation','Formation syndicale','Communication'], dir: 'debit' },
  { id: 'deplacement',       libelle: 'Frais de déplacement & hébergement',             categories: ['Déplacements'], dir: 'debit' },
  { id: 'restauration',      libelle: 'Frais de restaurations & location de salle',     categories: ['Réceptions / AG'], dir: 'debit' },
  { id: 'cotisation_unsfo',  libelle: "Cotisation à l'UNSFO",                           categories: ['Cotisation organisme'], dir: 'debit' },
  { id: 'subvention_versee', libelle: 'Subvention aux autres syndicats',                categories: ['Subvention versée'], dir: 'debit' },
];
export function computeLigne(ligne, comptabilite, year) {
  const ops = [];
  for (let m = 1; m <= 12; m++) {
    const mOps = comptabilite[year]?.[m]?.operations || [];
    mOps.forEach(op => {
      let match = false;
      if (ligne.types?.includes(op.type)) match = true;
      if (ligne.categories?.includes(op.categorie)) match = true;
      if (!match) return;
      const val = ligne.dir === 'credit' ? (op.credit || 0) : (op.debit || 0);
      if (val > 0.005) ops.push({ ...op, mois: m });
    });
  }
  const total = ops.reduce((s, o) => s + (ligne.dir === 'credit' ? (o.credit||0) : (o.debit||0)), 0);
  return { total: Math.round(total * 100) / 100, ops };
}

// ─── Fuzzy match opération → adhérent (scoring 0-100, gate NOM 2 chars) ──────
// Délègue à bankUtils.findBestMatch — élimine les faux positifs (DAGARD→BENARD, etc.)
export function matchAdherent(libelleBrut, type, adherents, montant = null) {
  if (!['A','B'].includes(type) || !adherents.length)
    return { status: 'none', adherentId: null, score: 0, reasons: [] };
  const { adherentId, score, reasons, status: raw } = findBestMatch(libelleBrut, adherents, montant);
  const status = raw === 'recognized' ? 'auto' : raw === 'review' ? 'approx' : 'none';
  return { status, adherentId: status === 'none' ? null : adherentId, score, reasons };
}
